import { useState, useEffect } from "react";
import { HashRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import {
  Menu,
  X,
  MapPin,
  Phone,
  Mail,
  Instagram,
  Youtube,
  Facebook,
  Globe,
} from "lucide-react";

import { BUSINESS, TRANSLATIONS } from "@/config/business";
import Home from "@/pages/Home";
import Gallery from "@/pages/Gallery";

// Language Switcher Component
function LanguageSwitcher({ lang, setLang }) {
  return (
    <button
      onClick={() => setLang(lang === "en" ? "mr" : "en")}
      className="flex items-center gap-2.5 px-4 py-2 rounded-full border border-[#D4AF37]/30 hover:border-[#D4AF37] bg-white/5 hover:bg-[#D4AF37]/10 transition-all group"
      title={lang === "en" ? "Switch to Marathi" : "इंग्रजीमध्ये बदला"}
    >
      <Globe size={14} className="text-[#D4AF37] group-hover:rotate-12 transition-transform" />
      <span className="text-[11px] font-bold tracking-wider text-gray-200">
        {lang === 'en' ? 'EN' : 'मराठी'}
      </span>
    </button>
  );
}

// Navigation Component
function Navigation({ lang, setLang }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const t = (key) => TRANSLATIONS[lang][key] || key;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (anchorId, e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      const element = document.getElementById(anchorId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
      setIsMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { type: "anchor", anchor: "services", label: t("services") },
    { type: "anchor", anchor: "equipment", label: t("equipment") },
    { type: "anchor", anchor: "about", label: t("about") },
    { type: "anchor", anchor: "releases", label: t("releases") },
    { type: "anchor", anchor: "testimonials", label: t("testimonials") },
    { type: "route", path: "/gallery", label: t("gallery") },
    { type: "anchor", anchor: "faq", label: t("faq") },
  ];

  const renderNavLink = (link, isMobile = false) => {
    const baseClass = isMobile
      ? "text-lg font-medium text-gray-200 hover:text-[#D4AF37] transition-colors py-2"
      : "text-sm font-medium text-gray-200 hover:text-[#D4AF37] transition-colors";

    if (link.type === "route") {
      return (
        <Link
          key={link.path}
          to={link.path}
          className={baseClass}
          onClick={() => isMobile && setIsMobileMenuOpen(false)}
        >
          {link.label}
        </Link>
      );
    } else {
      if (location.pathname === "/") {
        return (
          <a
            key={link.anchor}
            href={`#${link.anchor}`}
            onClick={(e) => handleNavClick(link.anchor, e)}
            className={baseClass}
          >
            {link.label}
          </a>
        );
      } else {
        return (
          <Link
            key={link.anchor}
            to="/"
            state={{ scrollTo: link.anchor }}
            className={baseClass}
            onClick={() => isMobile && setIsMobileMenuOpen(false)}
          >
            {link.label}
          </Link>
        );
      }
    }
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "glass-nav py-2" : "bg-transparent py-3"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex items-center gap-2">
          <img
            src="/images/ashastudioslogo.png"
            alt="Asha Studios Logo"
            className={`${isScrolled ? "h-14" : "h-24"} w-auto object-contain transition-all duration-500 hover:scale-105`}
            style={{ filter: "drop-shadow(0 0 12px rgba(212, 175, 55, 0.25))" }}
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => renderNavLink(link))}
          <LanguageSwitcher lang={lang} setLang={setLang} />
          {location.pathname === "/" ? (
            <a
              href="#booking"
              onClick={(e) => handleNavClick("booking", e)}
              data-testid="nav-cta-button"
              className="btn-primary px-5 py-2.5 rounded-md text-sm"
            >
              {t("bookSession")}
            </a>
          ) : (
            <Link
              to="/"
              state={{ scrollTo: "booking" }}
              data-testid="nav-cta-button"
              className="btn-primary px-5 py-2.5 rounded-md text-sm"
            >
              {t("bookSession")}
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-4 md:hidden">
          <LanguageSwitcher lang={lang} setLang={setLang} />
          <button
            data-testid="mobile-menu-button"
            className="text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden mobile-menu absolute top-full left-0 right-0 py-6 px-6"
        >
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => renderNavLink(link, true))}
            {location.pathname === "/" ? (
              <a
                href="#booking"
                onClick={(e) => handleNavClick("booking", e)}
                data-testid="mobile-nav-cta"
                className="btn-primary px-5 py-3 rounded-md text-center mt-2"
              >
                {t("bookSession")}
              </a>
            ) : (
              <Link
                to="/"
                state={{ scrollTo: "booking" }}
                data-testid="mobile-nav-cta"
                className="btn-primary px-5 py-3 rounded-md text-center mt-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("bookSession")}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

// Contact/Footer Section
function ContactSection({ lang }) {
  const t = (key) => TRANSLATIONS[lang][key] || key;
  return (
    <section id="contact" className="py-24 sm:py-32 bg-[#141415]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">{t("getInTouch")}</p>
            <h2 className="text-4xl sm:text-5xl tracking-tight font-bold text-white mb-6">{t("contactUs")}</h2>
            <p className="text-gray-200 leading-relaxed mb-8 max-w-lg">{t("contactSubtitle")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              <a href={`tel:${BUSINESS.phone}`} className="glass-panel rounded-xl p-5 flex items-center gap-4 hover:border-[#D4AF37]/30 transition-colors">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center"><Phone size={22} className="text-[#D4AF37]" /></div>
                <div>
                  <p className="text-sm text-gray-300">{t("callUs")}</p>
                  <p className="text-white font-medium">{BUSINESS.phone}</p>
                </div>
              </a>
              <a href={`mailto:${BUSINESS.email}`} className="glass-panel rounded-xl p-5 flex items-center gap-4 hover:border-[#D4AF37]/30 transition-colors">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center"><Mail size={22} className="text-[#D4AF37]" /></div>
                <div>
                  <p className="text-sm text-gray-300">{t("emailUs")}</p>
                  <p className="text-white font-medium">{BUSINESS.email}</p>
                </div>
              </a>
            </div>
            <a
              href={BUSINESS.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-panel rounded-xl p-5 flex items-start gap-4 hover:border-[#D4AF37]/30 transition-all group block"
            >
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                <MapPin size={22} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-sm text-gray-300 mb-1 flex items-center gap-1.5">
                  {t("visitUs")}
                  <span className="text-xs text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity">({t("openInMaps")})</span>
                </p>
                <p className="text-white font-medium">{BUSINESS.address}</p>
                <p className="text-sm text-gray-500 mt-1">Plus Code: {BUSINESS.plusCode}</p>
              </div>
            </a>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white mb-6">{t("followUsOn")}</h3>
            <div className="flex flex-col gap-4">
              <a href={BUSINESS.socials.instagram} target="_blank" rel="noopener noreferrer" className="glass-panel rounded-xl p-4 flex items-center gap-4 hover:border-[#D4AF37]/30 transition-colors"><Instagram size={24} className="text-[#D4AF37]" /><span className="text-white">Instagram</span></a>
              <a href={BUSINESS.socials.youtube} target="_blank" rel="noopener noreferrer" className="glass-panel rounded-xl p-4 flex items-center gap-4 hover:border-[#D4AF37]/30 transition-colors"><Youtube size={24} className="text-[#D4AF37]" /><span className="text-white">YouTube</span></a>
              <a href={BUSINESS.socials.facebook} target="_blank" rel="noopener noreferrer" className="glass-panel rounded-xl p-4 flex items-center gap-4 hover:border-[#D4AF37]/30 transition-colors"><Facebook size={24} className="text-[#D4AF37]" /><span className="text-white">Facebook</span></a>
            </div>
          </div>
        </div>
        
        {/* Responsive Map Embed */}
        <div className="mt-16 overflow-hidden rounded-2xl border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 shadow-2xl relative group">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3783.951174282406!2d74.1383732!3d18.4858707!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2e152fe6952b5%3A0xd39f392a7f4d062d!2sAsha%20Studios!5e0!3m2!1sen!2s!4v1780455950122!5m2!1sen!2s"
            width="100%"
            height="380"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Asha Studios Location Map"
            className="w-full block"
          />
          <div className="absolute bottom-4 right-4 z-10">
            <a
              href={BUSINESS.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#D4AF37] hover:bg-[#FBE18D] text-[#0A0A0B] px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-black/40 transition-all duration-300 transform hover:-translate-y-0.5"
            >
              <MapPin size={14} />
              {t("openInMaps")}
            </a>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2"><img src="/images/ashastudioslogo.png" alt="Logo" className="h-10 w-auto" style={{ filter: "drop-shadow(0 0 10px rgba(212, 175, 55, 0.2))" }} /></div>
            <div className="flex flex-col sm:items-end gap-1">
              <p className="text-sm text-gray-500">© {new Date().getFullYear()} Asha Studios. {t("allRightsReserved")}</p>
              <p className="text-xs text-gray-600">{t("designedDeveloped")} <a href="https://nirosha.org" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">Team Nirosha</a></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AppContent() {
  const [lang, setLang] = useState(() => {
    return localStorage.getItem("preferredLanguage") || "mr";
  });
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem("preferredLanguage", lang);
  }, [lang]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <Toaster position="top-right" toastOptions={{ style: { background: "#1A1A1C", color: "#F3F4F6", border: "1px solid rgba(255, 255, 255, 0.1)" } }} />
      <Navigation lang={lang} setLang={setLang} />
      <main>
        <Routes>
          <Route path="/" element={<Home lang={lang} />} />
          <Route path="/gallery" element={<Gallery lang={lang} />} />
        </Routes>
      </main>
      <ContactSection lang={lang} />
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
